import { FormValidator } from '@estee/elc-form';
import { action, computed, observable, set } from 'mobx';
import { StringSchema, string as yupString } from 'yup';
import Address, { AddressId } from '../Address';
import Config, { IDropdownOption, IDependentField } from '../Config';
import Constants, {
  ADDRESS_TYPE_ID,
  DEFAULT_ADDRESS_SUGGESTION_FIELDS,
  DEFAULT_AUTO_FILL_ADDRESS_FIELDS,
  FIELD_TYPE,
} from '../../../constants/Constants';
import { IAddressSchema } from '../../../api/JsonSchemaApi';
import PhoneFormStore, { IPhoneFormAttributes } from './PhoneFormStore';
import { IAddressAttributes } from '../Address';
import { isCountryUS } from '../../../utils/CountryUtils';
import { stringMask } from '../../../utils/StringMasking';
import ObjectUtils from '../../../utils/ObjectUtils';
import { getFieldDependenciesBySchema } from '../../../utils/AddressValidation';

export interface ICustomValidator {
  pattern: string;
  errorMessage: string;
}

interface IMapDropdownOptions {
  [key: string]: IDropdownOption[];
}

export interface IAddressFormStoreAttributes extends IPhoneFormAttributes, IAddressAttributes {}

/*
 Form Store to handle Shipping and Billing address forms stores
**/
class AddressFormStore extends PhoneFormStore<IAddressFormStoreAttributes> {

  @observable id: AddressId = '';

  @observable addressSchema: IAddressSchema;

  @observable config: Config;

  @observable private originalAddress: Address;

  @observable loadingLookupDataFields: Set<keyof IAddressAttributes> = new Set([]);

  static defaultEmptyValues: IAddressFormStoreAttributes = {
    [FIELD_TYPE.CITY]: '',
    [FIELD_TYPE.COLONY]: '',
    [FIELD_TYPE.COUNTRY]: '',
    [FIELD_TYPE.COUNTRY_ID]: null,
    [FIELD_TYPE.FIFTH_LINE_ADDRESS]: '',
    [FIELD_TYPE.FIRST_LINE_ADDRESS]: '',
    [FIELD_TYPE.FOURTH_LINE_ADDRESS]: '',
    [FIELD_TYPE.PHONE]: '',
    [FIELD_TYPE.MUNICIPALITY]: '',
    [FIELD_TYPE.SECOND_LINE_ADDRESS]: '',
    [FIELD_TYPE.SECOND_PHONE]: '',
    [FIELD_TYPE.STATE]: '',
    [FIELD_TYPE.THIRD_LINE_ADDRESS]: '',
    [FIELD_TYPE.ZIPCODE]: '',
  };

  constructor(config: Config) {
    super(config, {
      initialValues: AddressFormStore.defaultEmptyValues,
    });
    (this.formValidator as FormValidator).registerValidation('zipcodeExist', this.validateZipCodeExistence);
  }

  @computed get zipcodeMask(): string {
    // We shouldn't put country specific code but current schemas implementation
    // doesn't allow to customize postal code per billing address country.
    // This in particular was done to fix RCO-6080.
    // TODO: When the new JSON schemas are implemented remove this country specific code.
    if (isCountryUS(this.values[FIELD_TYPE.COUNTRY_ID])) {
      return '11111-1111';
    }
    return this.addressSchema?.properties[FIELD_TYPE.ZIPCODE]?.additionalProperties?.mask;
  }

  @computed get isTouched(): boolean {
    return !ObjectUtils.isEmpty(this.touched);
  }

  public getFieldAutoFillFields = (field: keyof IAddressAttributes): Array<keyof IAddressAttributes> => (
    this.addressSchema?.properties[field]?.additionalProperties?.address_auto_fill_fields
    || DEFAULT_AUTO_FILL_ADDRESS_FIELDS
  );

  public getFieldSuggestionFields = (field: keyof IAddressAttributes): Array<keyof IAddressAttributes> => (
    this.addressSchema?.properties[field]?.additionalProperties?.address_suggestion_fields
      || DEFAULT_ADDRESS_SUGGESTION_FIELDS
  );

  @action
  public setAddress(address: Address): AddressFormStore {
    this.originalAddress = address;
    this.id = address.id;

    this.setValues({
      ...address.attributes,
      // this might be temporary, just trying to support existing saved addresses
      [FIELD_TYPE.DEFAULT_SHIPPING]: address.attributes[FIELD_TYPE.DEFAULT_SHIPPING],
      [FIELD_TYPE.PHONE]: this.formatPhone(address.attributes[FIELD_TYPE.PHONE], address.attributes[FIELD_TYPE.COUNTRY_ID]),
      [FIELD_TYPE.SECOND_PHONE]: this.formatPhone(address.attributes[FIELD_TYPE.SECOND_PHONE], address.attributes[FIELD_TYPE.COUNTRY_ID]),
    });

    this.adjustFormValuesBasedOnSchema();

    return this;
  }

  public adjustFormValuesBasedOnSchema(): void {
    if (this.addressSchema) {
      this.setAddressType();
      this.computePhoneBasedOnSchema();
      this.adjustZipCodeBasedOnMask();
    }
  }

  private setAddressType(): void {
    const schemaProps = this.addressSchema.properties;
    if (schemaProps.address_type?.default) {
      this.setFieldValue(FIELD_TYPE.ADDRESS_TYPE, schemaProps.address_type.default as unknown as ADDRESS_TYPE_ID);
    }
  }

  public adjustZipCodeBasedOnMask(): void {
    if (this.zipcodeMask) {
      // apply masking to zipcode
      const updatedValue = stringMask(this.zipcodeMask, this.values[FIELD_TYPE.ZIPCODE]);
      this.setFieldValue(FIELD_TYPE.ZIPCODE, updatedValue);
    }
  }

  /**
   * toggle default shipping
   */
  @action
  public toggleIsDefault = (): void => {
    const defaultValue = this.values[FIELD_TYPE.DEFAULT_SHIPPING];

    this.setValues({
      [FIELD_TYPE.DEFAULT_SHIPPING]: !defaultValue,
    });
  };

  @action
  public addLoadingLookupDataField = (fieldName: keyof IAddressAttributes): void => {
    this.loadingLookupDataFields.add(fieldName);
  };

  @action
  public removeLoadingLookupDataField = (fieldName: keyof IAddressAttributes): void => {
    this.loadingLookupDataFields.delete(fieldName);
  };

  @action
  public clearLoadingLookupDataFields = (): void => {
    this.loadingLookupDataFields.clear();
  };

  @computed
  get schemaDropdownOptions(): IMapDropdownOptions {
    const schemaProperties = this.addressSchema?.properties || {};

    return (Object.keys(schemaProperties || {}) as Array<keyof typeof schemaProperties>).reduce((acc, current) => {
      const options: IDropdownOption[] = schemaProperties[current]?.oneOf?.map(item => ({
        label: item.label,
        value: item.pattern,
      }));

      return { ...acc, ...options && { [current]: options } };
    }, {});
  }

  @computed
  public get isRestrictedAddress(): boolean {
    const city: string = this.values.city.toUpperCase();

    return city === Constants.APO
      || city === Constants.DPO
      || city === Constants.FPO;
  }

  @computed
  public get shouldInvalidateCondensedState(): boolean {
    return this.showErrors || !!this.values[FIELD_TYPE.FIRST_LINE_ADDRESS];
  }

  public toAddress(markIdFromAttributes = false): Address {
    const address = new Address();
    address.id = this.id || address.id;
    address.setDefault(this.values[FIELD_TYPE.DEFAULT_SHIPPING]);

    set(address.attributes, this.values);

    // not sure, but maybe the id should be set each time the value is set
    // it feels like duplicate source of truth, but for now we will keep it as is
    if (markIdFromAttributes) {
      address.id = this.values[FIELD_TYPE.ID];
    }

    return address;
  }

  @computed
  public get locationHasChanged(): boolean {
    return this.originalAddress
      && (this.originalAddress.attributes[FIELD_TYPE.CITY] !== this.values[FIELD_TYPE.CITY]
      || this.originalAddress.attributes[FIELD_TYPE.ZIPCODE] !== this.values[FIELD_TYPE.ZIPCODE]
      || this.originalAddress.attributes[FIELD_TYPE.STATE] !== this.values[FIELD_TYPE.STATE]);
  }

  private validateZipCodeExistence = ({
    errorMessage = '',
    zipCode = '',
  }): StringSchema => {
    const regExp = new RegExp(zipCode);
    return yupString().matches(regExp, errorMessage);
  };

  resetFormValues(): void {
    this.values = AddressFormStore.defaultEmptyValues;
    this.errors = {};
    this.touched = {};
  }

  @computed
  public get hasLookupData(): boolean {
    return Object.values(this.addressSchema?.properties || {}).some(item => item.additionalProperties?.lookupData);
  }

  @computed
  public get onLoadLookupDataDependsOnFieldName(): string | undefined {
    const [, fieldSchema] = Object.entries(this.addressSchema?.properties || {})
      .find(([, value]) => value.additionalProperties?.lookupData?.onload) || [];
    
    return fieldSchema?.additionalProperties?.depends_on_field;
  }

  public clearDependentFields = (name: keyof IAddressAttributes): void => {
    const fieldDependencies = getFieldDependenciesBySchema(this.addressSchema.properties);

    if (fieldDependencies && fieldDependencies[name] && fieldDependencies[name].length > 0) {
      fieldDependencies[name].forEach((dependentField: IDependentField) => {
        const { fieldName } = dependentField;

        this.clearDependentFields(fieldName);
        this.setFieldValue(fieldName, '');
      });
    }
  };
}

export default AddressFormStore;
